import type { PublicSocialLink, PublicSocialNetwork } from '../../config/publicSocialLinks';
import { resolvePublicSocialLinks } from '../../config/publicSocialLinks';

const HOVER_CLASS: Record<PublicSocialNetwork, string> = {
  facebook: 'hover:border-[#1877F2]/50 hover:bg-[#1877F2]/15 hover:text-[#1877F2]',
  instagram: 'hover:border-pink-400/40 hover:bg-pink-500/10 hover:text-pink-300',
  linkedin: 'hover:border-[#0A66C2]/50 hover:bg-[#0A66C2]/15 hover:text-[#0A66C2]',
  youtube: 'hover:border-red-500/50 hover:bg-red-500/15 hover:text-red-400',
  x: 'hover:border-white/40 hover:bg-white/10 hover:text-white',
  tiktok: 'hover:border-[#69C9D0]/50 hover:bg-[#69C9D0]/10 hover:text-[#69C9D0]',
};

function SocialBrandIcon({ network, size = 18 }: { network: PublicSocialNetwork; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'currentColor',
    'aria-hidden': true as const,
  };

  switch (network) {
    case 'facebook':
      return (
        <svg {...common}>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      );
    case 'instagram':
      return (
        <svg {...common}>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      );
    case 'linkedin':
      return (
        <svg {...common}>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    case 'youtube':
      return (
        <svg {...common}>
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    case 'x':
      return (
        <svg {...common}>
          <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
        </svg>
      );
    case 'tiktok':
      return (
        <svg {...common}>
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z" />
        </svg>
      );
    default:
      return null;
  }
}

type Props = {
  className?: string;
  iconSize?: number;
  layout?: 'inline' | 'footer';
  /** Liens depuis l’API branding admin ; sinon variables d’environnement / défauts. */
  links?: readonly PublicSocialLink[];
};

export default function PublicSocialLinks({ className = '', iconSize = 18, layout = 'inline', links }: Props) {
  const resolvedLinks = resolvePublicSocialLinks(links);
  if (resolvedLinks.length === 0) return null;

  const isFooter = layout === 'footer';

  return (
    <div className={className}>
      <p
        className={
          isFooter
            ? 'text-[11px] font-bold uppercase tracking-[0.18em] text-white/40'
            : 'mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white/50'
        }
      >
        {isFooter ? 'Réseaux sociaux' : 'Suivez-nous'}
      </p>
      {isFooter ? (
        <p className="mt-2 text-sm leading-relaxed text-white/55">
          Retrouvez nos actualités, cohortes et conseils formation.
        </p>
      ) : null}
      <ul
        className={`flex flex-nowrap items-center gap-2 sm:gap-2.5 ${
          isFooter ? 'mt-5' : 'justify-center sm:justify-start'
        }`}
      >
        {resolvedLinks.map((link) => (
          <li key={link.id} className="shrink-0">
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={link.label}
              title={link.label}
              className={`flex items-center justify-center border border-white/10 bg-white/[0.04] text-white/70 transition duration-200 ${HOVER_CLASS[link.id]} ${
                isFooter ? 'h-11 w-11 rounded-public-btn' : 'h-10 w-10 rounded-full'
              }`}
            >
              <SocialBrandIcon network={link.id} size={iconSize} />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
